// src/components/layout/Navbar.tsx
// Sivrce-style layout: logo far-left · center pill nav · right actions cluster.
// New: LangPill (ENG ⇄ GEO) + ThemeToggle (sun/moon) in the right cluster.

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuthStore } from '../../stores/useAuthStore';
import { usePaymentStore } from '../../stores/usePaymentStore';
import { useThemeStore } from '../../stores/useThemeStore';
import { useLanguageStore } from '../../stores/useLanguageStore';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useT } from '../../data/translations';
import { useSubscription } from '../../hooks/useSubscription';
import { SyncIndicator } from './SyncIndicator';
import { MobileMenu } from './MobileMenu';
import { StreakBadge } from '../streak/StreakBadge';

const MOBILE_BP = 768;

// ── SVG icons ─────────────────────────────────────────────────────────────────

function SunIcon({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon({ size = 15, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function GlobeIcon({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
}

// ── ThemeToggle ───────────────────────────────────────────────────────────────

function ThemeToggle() {
  const C = useThemePalette();
  const { theme, toggle } = useThemeStore();
  const [hover, setHover] = useState(false);
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggle}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          '36px',
        height:         '36px',
        borderRadius:   '999px',
        background:     hover ? (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)') : 'transparent',
        border:         `1px solid ${hover ? C.borderH : C.border}`,
        color:          hover ? C.accent : C.muted,
        cursor:         'pointer',
        transition:     'all 0.2s ease',
        transform:      hover ? 'translateY(-1px)' : 'none',
        boxShadow:      hover ? `0 4px 12px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}` : 'none',
        fontFamily:     'inherit',
      }}
    >
      {isDark ? <SunIcon size={15} color="currentColor" /> : <MoonIcon size={14} color="currentColor" />}
    </button>
  );
}

// ── LangPill ──────────────────────────────────────────────────────────────────

function LangPill() {
  const C = useThemePalette();
  const { lang, toggle } = useLanguageStore();
  const [hover, setHover] = useState(false);
  const isDark = useThemeStore((s) => s.theme) === 'dark';

  return (
    <button
      onClick={toggle}
      title={lang === 'en' ? 'Switch to Georgian' : 'Switch to English'}
      aria-label={lang === 'en' ? 'Switch to Georgian' : 'Switch to English'}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display:       'inline-flex',
        alignItems:    'center',
        gap:           '5px',
        padding:       '6px 12px 6px 10px',
        borderRadius:  '999px',
        background:    hover
          ? (isDark ? 'rgba(79,140,247,0.14)' : 'rgba(79,140,247,0.10)')
          : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
        border:        `1px solid ${hover ? 'rgba(79,140,247,0.40)' : C.border}`,
        color:         hover ? C.accent : C.muted,
        fontSize:      '0.7rem',
        fontWeight:    700,
        letterSpacing: '0.07em',
        cursor:        'pointer',
        fontFamily:    'inherit',
        transition:    'all 0.2s ease',
        transform:     hover ? 'translateY(-1px)' : 'none',
        boxShadow:     hover ? '0 4px 14px rgba(79,140,247,0.18)' : 'none',
        whiteSpace:    'nowrap',
      }}
    >
      <GlobeIcon size={13} color="currentColor" />
      {lang === 'en' ? 'ENG' : 'GEO'}
    </button>
  );
}

// ── NavLinkBtn (pill nav version) ─────────────────────────────────────────────

interface NavLinkBtnProps {
  to?: string;
  onClick?: () => void;
  children: React.ReactNode;
  active?: boolean;
  premium?: boolean;
}

function NavLinkBtn({ to, onClick, children, active, premium }: NavLinkBtnProps) {
  const C = useThemePalette();
  const [hover, setHover] = useState(false);
  const isDark = useThemeStore((s) => s.theme) === 'dark';

  const style: React.CSSProperties = {
    display:        'inline-flex',
    alignItems:     'center',
    padding:        '7px 14px',
    fontSize:       '0.72rem',
    fontWeight:     700,
    letterSpacing:  '0.07em',
    textTransform:  'uppercase',
    color:          premium ? C.premium : active ? C.text : hover ? C.text : C.muted,
    background:     active
      ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')
      : hover
        ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')
        : 'transparent',
    border:         'none',
    borderRadius:   '999px',
    textDecoration: 'none',
    cursor:         'pointer',
    transition:     'color 0.2s ease, background 0.2s ease',
    fontFamily:     'inherit',
    whiteSpace:     'nowrap',
  };

  const handlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };

  if (to) return <Link to={to} style={style} {...handlers}>{children}</Link>;
  return <button style={style} onClick={onClick} {...handlers}>{children}</button>;
}

// ── AuthBtn (solid/ghost pill) ────────────────────────────────────────────────

interface AuthBtnProps {
  variant: 'ghost' | 'solid';
  onClick: () => void;
  children: React.ReactNode;
}

function AuthBtn({ variant, onClick, children }: AuthBtnProps) {
  const C = useThemePalette();
  const [hover, setHover] = useState(false);
  const isSolid = variant === 'solid';
  const isDark  = useThemeStore((s) => s.theme) === 'dark';

  return (
    <button
      style={{
        padding:       '8px 20px',
        borderRadius:  '999px',
        fontSize:      '0.75rem',
        fontWeight:    700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        cursor:        'pointer',
        border: isSolid
          ? 'none'
          : `1px solid ${hover ? C.borderH : C.border}`,
        background: isSolid
          ? (hover
              ? `linear-gradient(135deg, #5a96f8 0%, #7c6ff7 100%)`
              : `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`)
          : (hover
              ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
              : 'transparent'),
        color:      isSolid ? '#fff' : hover ? C.text : C.muted,
        transition: 'all 0.22s ease',
        fontFamily: 'inherit',
        transform:  hover ? 'translateY(-2px)' : 'none',
        boxShadow:  isSolid && hover ? '0 6px 22px rgba(99,102,241,0.42)' : 'none',
      }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
    </button>
  );
}

// ── UserPill ──────────────────────────────────────────────────────────────────

interface UserPillProps {
  displayName: string;
  avatarUrl:   string | null;
  onClick:     () => void;
}

function UserPill({ displayName, avatarUrl, onClick }: UserPillProps) {
  const C = useThemePalette();
  const [hover, setHover] = useState(false);
  const initial   = (displayName || '?').charAt(0).toUpperCase();
  const shortName = displayName.length > 18 ? displayName.slice(0, 16) + '…' : displayName;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display:      'inline-flex',
        alignItems:   'center',
        gap:          '10px',
        padding:      '5px 14px 5px 5px',
        borderRadius: '999px',
        background:   hover ? (useThemeStore.getState().theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') : 'transparent',
        border:       `1px solid ${hover ? C.borderH : C.border}`,
        cursor:       'pointer',
        transition:   'all 0.2s ease',
        fontFamily:   'inherit',
        transform:    hover ? 'translateY(-1px)' : 'none',
      }}
      aria-label="Open profile"
    >
      <span style={{
        width:          '28px',
        height:         '28px',
        borderRadius:   '50%',
        background:     avatarUrl ? 'transparent' : `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        color:          '#fff',
        fontWeight:     700,
        fontSize:       '0.78rem',
        overflow:       'hidden',
        flexShrink:     0,
        boxShadow:      hover ? `0 0 0 2px rgba(99,102,241,0.42)` : 'none',
        transition:     'box-shadow 0.2s ease',
      }}>
        {avatarUrl
          ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : initial}
      </span>
      <span style={{ color: C.text, fontSize: '0.82rem', fontWeight: 600, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {shortName}
      </span>
    </button>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────

export function Navbar() {
  const location        = useLocation();
  const C               = useThemePalette();
  const t               = useT();
  const theme           = useThemeStore((s) => s.theme);
  const isDark          = theme === 'dark';
  const user             = useAuthStore((s) => s.user);
  const profile          = useAuthStore((s) => s.profile);
  const openAuthModal    = useAuthStore((s) => s.openAuthModal);
  const openProfileModal = useAuthStore((s) => s.openProfileModal);
  const openPaymentModal = usePaymentStore((s) => s.openModal);
  const { isSubscribed } = useSubscription();

  const [isMobile, setIsMobile] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1024) < MOBILE_BP,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (location.pathname === '/exercise') return null;

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Navbar bar style — glassmorphism on dark, clean white on light
  const barStyle: React.CSSProperties = {
    position:             'sticky',
    top:                  0,
    zIndex:               50,
    width:                '100%',
    height:               '64px',
    background:           isDark
      ? (scrolled ? 'rgba(8,12,20,0.96)' : 'rgba(8,12,20,0.70)')
      : (scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.85)'),
    backdropFilter:       'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderBottom:         `1px solid ${scrolled ? C.borderH : C.border}`,
    transition:           'background 0.3s ease, border-color 0.3s ease',
    boxShadow:            scrolled && !isDark ? '0 1px 12px rgba(0,0,0,0.08)' : 'none',
  };

  const innerStyle: React.CSSProperties = {
    maxWidth:       '1280px',
    height:         '100%',
    margin:         '0 auto',
    padding:        '0 28px',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    gap:            '16px',
  };

  const logoImgStyle: React.CSSProperties = {
    height: '34px', width: 'auto', objectFit: 'contain', display: 'block',
  };
  const logoFbStyle: React.CSSProperties = {
    display:              'none',
    fontSize:             '18px',
    fontWeight:           800,
    letterSpacing:        '0.08em',
    textTransform:        'uppercase',
    background:           `linear-gradient(135deg, ${C.text} 0%, ${C.accent} 50%, ${C.accent2} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor:  'transparent',
    backgroundClip:       'text',
  };

  const handleLogoError = (fbId: string) => (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).style.display = 'none';
    const fb = document.getElementById(fbId);
    if (fb) fb.style.display = 'inline';
  };

  // ── Center pill container ───────────────────────────────────────────────
  const navPillStyle: React.CSSProperties = {
    display:              'flex',
    alignItems:           'center',
    gap:                  '2px',
    padding:              '5px 6px',
    borderRadius:         '999px',
    background:           C.navPill,
    border:               `1px solid ${C.border}`,
    backdropFilter:       'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  };

  // ── Right cluster ───────────────────────────────────────────────────────
  const rightClusterStyle: React.CSSProperties = {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
    flexShrink: 0,
  };

  // ─────────────── DESKTOP ────────────────────────────────────────────────
  if (!isMobile) {
    return (
      <nav style={barStyle} role="navigation" aria-label="Main">
        <div style={innerStyle}>

          {/* Logo — far left */}
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }} aria-label="ESP for Sports — home">
            <img src="/images/logo.png" alt="ESP for Sports" style={logoImgStyle} onError={handleLogoError('nav-fb-desk')} />
            <span id="nav-fb-desk" style={logoFbStyle}>START</span>
          </Link>

          {/* Center pill nav */}
          <div style={navPillStyle}>
            <NavLinkBtn to="/about"    active={isActive('/about')}>   {t('NAV_ABOUT')}    </NavLinkBtn>
            <NavLinkBtn to="/"         active={isActive('/')}>        {t('NAV_BOOKS')}    </NavLinkBtn>
            <NavLinkBtn to="/partners" active={isActive('/partners')}>{t('NAV_PARTNERS')} </NavLinkBtn>
            <NavLinkBtn to="/contact"  active={isActive('/contact')}>  {t('NAV_CONTACT')}  </NavLinkBtn>
          </div>

          {/* Right cluster */}
          <div style={rightClusterStyle}>
            <SyncIndicator />
            <StreakBadge size="sm" />
            <ThemeToggle />
            <LangPill />

            {!isSubscribed && (
              <button
                onClick={openPaymentModal}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(168,85,247,0.40)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'none';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 10px rgba(168,85,247,0.25)';
                }}
                style={{
                  padding:       '7px 16px',
                  borderRadius:  '999px',
                  background:    `linear-gradient(135deg, ${C.accent3} 0%, ${C.accent2} 100%)`,
                  color:         '#fff',
                  fontWeight:    700,
                  fontSize:      '0.72rem',
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  border:        'none',
                  cursor:        'pointer',
                  fontFamily:    'inherit',
                  transition:    'all 0.22s ease',
                  boxShadow:     '0 2px 10px rgba(168,85,247,0.25)',
                }}
              >
                {t('NAV_PREMIUM')}
              </button>
            )}

            {!user ? (
              <>
                <AuthBtn variant="ghost" onClick={() => openAuthModal('login')}>  {t('NAV_LOGIN')}  </AuthBtn>
                <AuthBtn variant="solid" onClick={() => openAuthModal('signup')}>{t('NAV_SIGNUP')}</AuthBtn>
              </>
            ) : (
              <UserPill
                displayName={profile?.displayName || user.email || 'User'}
                avatarUrl={profile?.avatarUrl ?? null}
                onClick={openProfileModal}
              />
            )}
          </div>
        </div>
      </nav>
    );
  }

  // ─────────────── MOBILE ─────────────────────────────────────────────────
  return (
    <>
      <nav style={barStyle} role="navigation" aria-label="Main">
        <div style={{ ...innerStyle, padding: '0 20px' }}>

          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }} aria-label="ESP for Sports — home">
            <img src="/images/logo.png" alt="ESP for Sports" style={logoImgStyle} onError={handleLogoError('nav-fb-mob')} />
            <span id="nav-fb-mob" style={logoFbStyle}>START</span>
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SyncIndicator />
            <ThemeToggle />
            <LangPill />
            <button
              style={{
                background:    isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border:        `1px solid ${C.border}`,
                borderRadius:  '10px',
                width:         '40px',
                height:        '40px',
                display:       'flex',
                flexDirection: 'column',
                alignItems:    'center',
                justifyContent:'center',
                gap:           '5px',
                cursor:        'pointer',
                padding:       0,
              }}
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              {[0,1,2].map(i => (
                <span key={i} style={{
                  display:     'block',
                  width:       i === 1 ? '14px' : '18px',
                  height:      '1.5px',
                  background:  C.muted,
                  borderRadius:'1px',
                }} />
              ))}
            </button>
          </div>
        </div>
      </nav>
      <MobileMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}